import React, { forwardRef } from "react";
import { arrayOf, bool, func, object, oneOfType, string } from "prop-types";
import { useTranslation } from "@lib/i18n";

import FilterResultList from "@components/TestDrive/FilterResultList";
import TestDriveTypesList from "@components/TestDrive/TestDriveTypesList";
import CollapsableContainer from "@components/shared/CollapsableContainer";

import {
  addressType,
  testDriveLocationType,
  testDriveType,
  testDriveVehicleType,
} from "@types";

const TestDrivePointsSection = forwardRef(function TestDrivePointsSection(
  props,
  ref
) {
  const {
    affiliation,
    testDrivePoints,
    address,
    selectedTestDrivePoint,
    typeFilter,
    setTypeFilter,
    typesContainerRef,
    isTypeExplanationOpen,
    setIsTypeExplanationOpen,
    testDriveTypes,
  } = props;

  const { t } = useTranslation();

  // Move selected test drive point to the top
  const sortedTestDrivePoints = testDrivePoints.sort((a, b) => {
    const selectedPointId = selectedTestDrivePoint?._id;

    if (a._id === selectedPointId) {
      return -1;
    }

    if (b._id === selectedPointId) {
      return 1;
    }

    return 0;
  });

  return (
    <div
      className="flex flex-col justify-between p-4 pt-0 gap-8 relative lg:px-8 lg:min-h-100"
      ref={ref}
    >
      {testDrivePoints.length > 0 && (
        <FilterResultList
          activePointId={selectedTestDrivePoint?._id}
          isOpen={Boolean(address || typeFilter || selectedTestDrivePoint)}
          testDrivePoints={sortedTestDrivePoints}
        />
      )}
      <CollapsableContainer
        data-id="small-accordion"
        className={{ wrap: "!p-0" }}
        title={t("testDrive.try")}
        ref={typesContainerRef}
        isOpen={isTypeExplanationOpen}
        onChange={isOpen => {
          setIsTypeExplanationOpen(!isOpen);
        }}
      >
        <TestDriveTypesList
          testDriveTypes={testDriveTypes}
          setTypeFilter={setTypeFilter}
          affiliation={affiliation}
        />
      </CollapsableContainer>
    </div>
  );
});

TestDrivePointsSection.displayName = "TestDrivePointsSection";

TestDrivePointsSection.propTypes = {
  typesContainerRef: object.isRequired,
  isTypeExplanationOpen: bool.isRequired,
  setIsTypeExplanationOpen: func.isRequired,
  testDriveTypes: arrayOf(testDriveType).isRequired,
  address: addressType,
  affiliation: string,
  typeFilter: string,
  setTypeFilter: func,
  testDrivePoints: arrayOf(
    oneOfType([testDriveVehicleType, testDriveLocationType])
  ).isRequired,
  selectedTestDrivePoint: oneOfType([
    testDriveVehicleType,
    testDriveLocationType,
  ]),
};

export default TestDrivePointsSection;
